const DOCUMENT_NUMBER_PATTERN = /^[0-9]{6,10}$/;

/**
 * Strips everything but digits from a DNI, mirroring
 * `api/src/gym/member-service.js#parseDocumentNumber` (which additionally
 * strips only dots/spaces server-side, but any other punctuation a paste
 * might carry — dashes, for instance — is never a valid DNI character
 * either, so dropping every non-digit here is strictly safer and always
 * agrees with what the server ends up persisting). Never throws: an empty
 * or all-punctuation value normalizes to `''`.
 */
export function normalizeDocumentNumber(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/[^0-9]/g, '');
}

/**
 * Presents a normalized DNI with the Argentine thousands-grouping dots the
 * spec asks for ("formato visual 30.123.456"), capped at the server's own
 * 10-digit maximum so the field never displays more than it could ever send.
 * Purely a display transform — the value that travels to the API is always
 * `normalizeDocumentNumber`'s plain-digits result, never this string.
 */
export function formatDocumentNumber(value) {
  const digits = normalizeDocumentNumber(value).slice(0, 10);
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Mirrors the server's own `DOCUMENT_NUMBER_PATTERN` (6-10 digits) so a form
 * can reject an obviously-too-short/too-long DNI before a round trip; an
 * empty string is valid here (DNI is optional — a blank field clears it,
 * same convention as `fitnessGoal`).
 */
export function isValidDocumentNumber(digits) {
  return digits === '' || DOCUMENT_NUMBER_PATTERN.test(digits);
}
